/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//


const __vue_script__ = {
    name: 'GlobalHotkeys',

    props: {
        enabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            debug: false,
            params: {},
            documentListener: null,
            bindings: {},
            helps: []
        };
    },

    watch: {
        bindings: {
            immediate: true,
            deep: true,
            handler() {
                const helps = [];
                for (const [shortcut, value] of Object.entries(this.bindings)) {
                    helps.push({ shortcut, description: value?.description });
                }
                this.helps = helps;
            }
        },

        enabled: {
            immediate: true,
            handler(value) {
                if (!this.documentListener) {
                    this.documentListener = this.onKeydown.bind(this);
                    document.addEventListener('keydown', this.documentListener);

                    this.bind('shift + ?', this.showHelp, 'Shows this help');
                }

                if (value) {
                    this.$emit('ready');
                }
            }
        }
    },

    beforeDestroy() {
        if (this.documentListener) {
            document.removeEventListener('keydown', this.documentListener);
        }
    },

    methods: {
        /**
         * Handler for the global keyboard event
         *
         * @param {KeyboardEvent} event
         */
        onKeydown(event) {
            if (this.debug) {
                console.debug('[GlobalHotkeys] New event received', event);
            }

            if (this.shouldStop(event)) {
                return;
            }

            for (const [binding, { handler }] of Object.entries(this.bindings)) {
                if (this.eventMatches(event, binding)) {
                    handler(event);
                }
            }
        },

        /**
         * Binds a keyboard shortcut to an function or method
         *
         * @param {string} shortcut the shortcut to bind to the handler
         * @param {function(KeyboardEvent)} handler the handler for the shortcut
         * @param {string} [description] an optional short description of the action
         *
         * @public
         */
        bind(shortcut, handler, description) {
            if (!this.enabled) {
                console.warn('[GlobalHotkeys] No bindings allowed since the component is disabled');
                return;
            }

            this.$set(this.bindings, shortcut, {
                handler,
                description
            });
        },

        /**
         * Removes the specified binding from the pool
         *
         * @param {string} shortcut the shortcut to bind to the handler
         *
         * @public
         */
        unbind(shorcut) {
            delete this.bindings[shorcut];
        },

        /**
         * Checks if the given event matches with the shortcut
         *
         * @param {KeyboardEvent} event
         * @param {string} shortcut
         *
         * @returns {boolean} true if the event matches the given shorcut
         */
        eventMatches(event, shortcut) {
            let match = false;
            const aliases = shortcut.split(',').map(i => i.trim());
            for (const alias of aliases) {
                const strokes = new Set(alias.split('+').map(i => i.trim()));
                const key = Array.from(strokes).find(k => !['ctrl', 'cmd', 'shift', 'alt'].includes(k));
                if (key === '?') {
                    strokes.add('shift');
                }

                const keyCode = KEYS_MAP[key];

                const eventMods = {
                    ctrl: event.ctrlKey,
                    cmd: event.metaKey,
                    shift: event.shiftKey,
                    alt: event.altKey
                };

                const shortcutMods = {
                    ctrl: strokes.has('ctrl'),
                    cmd: strokes.has('cmd'),
                    shift: strokes.has('shift'),
                    alt: strokes.has('alt'),
                };

                const hasAllFeats = Object.keys(eventMods).every(mod => eventMods[mod] === shortcutMods[mod]);

                match = hasAllFeats && event.code === keyCode;

                if (match) {
                    break;
                }
            }

            return match;
        },

        /**
         * Checks whether the handling should be stopped or not
         *
         * @param {KeyboardEvent} event
         *
         * @returns {boolean} true if the event cannot continue
         */
        shouldStop(event) {
            const { target: { id, isConnected, isContentEditable, tagName } } = event;
            const isAttachedToDom = isConnected;
            const isClipboard = id === 'clipboard';
            const isInputElement = (!isClipboard && ['input', 'select', 'textarea'].includes(tagName?.toLowerCase())) || isContentEditable;
            return isAttachedToDom && isInputElement;
        },

        /**
         * Shows the help modal if there are any bindings
         */
        showHelp() {
            if (Object.keys(this.bindings).filter(b => b !== 'shift + ?').length) {
                this.$modal.show('help');
            } else {
                console.warn('[GlobalHotkeys] No bindings setup');
            }
        }
    }
};

const KEYS_MAP = {
    0: 'Digit1', 1: 'Digit2', 2: 'Digit3', 3: 'Digit4', 4: 'Digit5', 5: 'Digit6', 6: 'Digit7', 7: 'Digit8', 8: 'Digit9', 9: 'Digit0',

    a: 'KeyA', b: 'KeyB', c: 'KeyC', d: 'KeyD', e: 'KeyE', f: 'KeyF', g: 'KeyG', h: 'KeyH', i: 'KeyI', j: 'KeyJ', k: 'KeyK', l: 'KeyL',
    m: 'KeyM', n: 'KeyN', o: 'KeyO', p: 'KeyP', q: 'KeyQ', r: 'KeyR', s: 'KeyS', t: 'KeyT', u: 'KeyU', v: 'KeyV', w: 'KeyW', x: 'KeyX',
    y: 'KeyY', z: 'KeyZ',

    comma: 'Comma', period: 'Period', semicolon: 'Semicolon', quote: 'Quote', bracket_left: 'BracketLeft', bracket_right: 'BracketRight',
    backquote: 'Backquote', backslash: 'Backslash', minus: 'Minus', equal: 'Equal', enter: 'Enter', space: 'Space', tab: 'Tab',
    delete: 'Delete', end: 'End', home: 'Home', insert: 'Insert', page_down: 'PageDown', page_up: 'PageUp', arrow_down: 'ArrowDown',
    arrow_left: 'ArrowLeft', arrow_right: 'ArrowRight', arrow_up: 'ArrowUp', escape: 'Escape', print_screen: 'PrintScreen',
    scroll_lock: 'ScrollLock', pause: 'Pause',

    f1: 'F1', f2: 'F2', f3: 'F3', f4: 'F4', f5: 'F5', f6: 'F6', f7: 'F7', f8: 'F8', f9: 'F9', f10: 'F10', f11: 'F11', f12: 'F12',
    f13: 'F13', f14: 'F14', f15: 'F15', f16: 'F16', f17: 'F17', f18: 'F18', f19: 'F19', f20: 'F20', f21: 'F21', f22: 'F22', f23: 'F23',
    f24: 'F24',

    '?': 'Slash'
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.enabled)?_c('div',{staticClass:"global-hotkeys"},[_c('modal',{attrs:{"name":"help","classes":"small","height":"auto"}},[_c('div',{staticClass:"modal-header"},[_c('a',{staticClass:"close",on:{"click":function($event){return _vm.$modal.hide('help')}}}),_c('h2',[_vm._v("Keyboard shortcuts")])]),_c('div',{staticClass:"modal-body"},[_c('div',{staticClass:"table-wrapper"},[_c('table',{staticClass:"table-striped"},[_c('tbody',_vm._l((_vm.helps),function(help){return _c('tr',[_c('th',{staticClass:"shortcut"},[_vm._v(_vm._s(help.shortcut))]),_c('td',[_vm._v(_vm._s(help.description))])])}),0)])])])])],1):_vm._e()}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "GlobalHotkeys.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__